/**
 * How a unit becomes a login.
 *
 * Firebase Auth needs an email address, so one is synthesised from the org code
 * and the unit. It never receives mail and the resident never sees it: they type
 * the code from their slip and the number on their door.
 *
 * The first password is derived from those same two values rather than randomly
 * generated, because it is not what protects the account. Phone verification at
 * first login is. That also means a credential slip carries no secret and can be
 * reprinted freely.
 *
 * Every rule here is shared by the app, the roster import and the printed slips.
 * They must agree exactly or a resident cannot sign in.
 */

const NON_ALPHANUMERIC = /[^a-z0-9]/g;

export class IdentityError extends Error {}

export function contactName(value: string): string {
  const name = value.trim().replace(/\s+/g, " ");
  if (
    name.length < 2 ||
    name.length > 120 ||
    [...name].some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)
  ) {
    throw new IdentityError("Enter a name between 2 and 120 characters");
  }
  return name;
}

/**
 * A phone number, stored with its country code whichever way it was typed.
 * Everyone here is in Ethiopia, so nobody is asked for +251: a mobile number
 * written 0911 234 567, 911 234 567 or 251 911 234 567 is completed with it.
 * A number already written with a + and another country's code is kept.
 */
export function contactPhone(value: string): string {
  let phone = value.trim().replace(/[\s()-]/g, "");
  if (/^0[79]\d{8}$/.test(phone)) {
    phone = `+251${phone.slice(1)}`;
  } else if (/^[79]\d{8}$/.test(phone)) {
    phone = `+251${phone}`;
  } else if (/^251[79]\d{8}$/.test(phone)) {
    phone = `+${phone}`;
  }
  if (!/^\+[1-9]\d{7,14}$/.test(phone)) {
    throw new IdentityError("Enter a mobile number like 0911 234 567");
  }
  return phone;
}

/** Codes keep the company's initial readable and reserve six digits per complex. */
export function complexCode(realEstateName: string, number: number): string {
  const initial = realEstateName.trim().charAt(0).toUpperCase();
  if (!/^[A-Z]$/.test(initial)) {
    throw new IdentityError("Enter the real estate company's name starting with a Latin letter");
  }
  if (!Number.isInteger(number) || number < 1 || number > 999999) {
    throw new IdentityError("A complex number must be between 1 and 999999");
  }
  return `${initial}${String(number).padStart(6, "0")}`;
}

/**
 * True for an ID onboarding assigned. Complexes created before assignment keep
 * their hand-picked ID for good; renaming a tenant would mean reissuing every
 * login and moving every document under it.
 */
export function isAssignedComplexCode(orgId: string): boolean {
  return /^[a-z]\d{6}$/.test(normalizeOrgCode(orgId));
}

export function normalizeOrgCode(code: string): string {
  const normalized = code.trim().toLowerCase().replace(NON_ALPHANUMERIC, "");
  if (normalized.length < 3) {
    throw new IdentityError(`An org code needs at least 3 characters, received "${code}"`);
  }
  return normalized;
}

/** Unit ids are shown the way they appear on the door: "B4-203". */
export function normalizeUnitId(unitId: string): string {
  const normalized = unitId.trim().toUpperCase().replace(/\s+/g, "");
  if (!/^[A-Z0-9]+(-[A-Z0-9]+)*$/.test(normalized)) {
    throw new IdentityError(`"${unitId}" is not a unit id`);
  }
  return normalized;
}

export function loginEmail(orgCode: string, unitId: string, domain: string): string {
  const org = normalizeOrgCode(orgCode);
  const unit = normalizeUnitId(unitId).toLowerCase();
  const host = domain
    .trim()
    .toLowerCase()
    .replace(/^\.+|\.+$/g, "");
  if (!host.includes(".")) {
    throw new IdentityError(`"${domain}" is not a domain`);
  }
  return `${unit}@${org}.${host}`;
}

/** Lowercase, without 0/o, 1/l/i, so a code read off paper is typed right. */
const ACTIVATION_ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";
const ACTIVATION_LENGTH = 10;

/**
 * The one-time password printed on a slip. It is the only secret between the
 * slip and the account until first login replaces it, so it has to be random:
 * 31^10 is about 8 * 10^14 codes. The caller supplies the randomness, a CSPRNG
 * such as node's `crypto.randomInt`, because this package runs on phones too.
 */
export function activationCode(randomBelow: (max: number) => number): string {
  let code = "";
  for (let i = 0; i < ACTIVATION_LENGTH; i++) {
    code += ACTIVATION_ALPHABET.charAt(randomBelow(ACTIVATION_ALPHABET.length));
  }
  return code;
}

export type PasswordProblem = "tooShort" | "needsLetterAndNumber";

/** What a resident's chosen password must clear at first login. */
export function passwordProblems(password: string): PasswordProblem[] {
  const problems: PasswordProblem[] = [];
  if (password.length < 8) {
    problems.push("tooShort");
  }
  if (!(/[a-zA-Z]/.test(password) && /[0-9]/.test(password))) {
    problems.push("needsLetterAndNumber");
  }
  return problems;
}

/** The deep link a credential slip's QR code encodes. */
export function slipDeepLink(orgCode: string, unitId: string, scheme = "liyu"): string {
  const params = new URLSearchParams({
    org: normalizeOrgCode(orgCode),
    unit: normalizeUnitId(unitId),
  });
  return `${scheme}://login?${params.toString()}`;
}

const TERM_PATTERN = /^term-(\d+)$/;

/**
 * The next occupancy of a unit.
 *
 * A term is what scopes a resident to their own tenancy: reminting it is what
 * stops a new occupant reading the last one's ledger, and it is the only thing
 * a handover has to get right. Numbers count up so a statement can be read in
 * order; anything that does not follow the pattern starts a fresh count rather
 * than guessing at what came before.
 */
export function nextTermId(current: string | undefined): string {
  const matched = current?.match(TERM_PATTERN);
  if (!matched) {
    return "term-1";
  }
  return `term-${String(Number(matched[1]) + 1)}`;
}
